import { Divider } from "components/design/Divider";
import {
  OptionData,
  Combobox,
} from "components/DesignSystem/Combobox/Combobox";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import { datePeriod as defaultDatePeriod } from "constants/bookkeeping";
import {
  DISPLAY_DATE,
  FILTER_DATE_INPUT_FORMAT,
  MMM_YYYY,
  MONTH_FORMAT,
  Q_YYYY,
  QUARTER_FORMAT,
  YEAR_FORMAT,
  YYYY,
  YYYY_MM_DD,
} from "constants/date";
import dayjs, { Dayjs } from "dayjs";
import { Formik, Form, Field, FieldProps } from "formik";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import { MultiValue, SingleValue } from "react-select";
import { getDateRange, DateRangeValue } from "utils/getDateRange";

const pickerTypeProps = {
  date: {},
  month: { showMonthYearPicker: true },
  quarter: { showQuarterYearPicker: true },
  year: { showYearPicker: true },
};

export const dateFormats = {
  date: FILTER_DATE_INPUT_FORMAT,
  month: MONTH_FORMAT,
  quarter: QUARTER_FORMAT,
  year: YEAR_FORMAT,
};

const datePlaceholder = {
  date: DISPLAY_DATE,
  month: MMM_YYYY,
  quarter: Q_YYYY,
  year: YYYY,
};

type DateFilterProps = {
  updateFilter: <S extends "START_DATE" | "END_DATE" | "SELECT_PERIOD">(
    name: S,
    newValue: {
      START_DATE: string;
      END_DATE: string;
      SELECT_PERIOD: string;
    }[S]
  ) => void;
  values: {
    START_DATE: string;
    END_DATE: string;
    SELECT_PERIOD: string;
  };
  datePeriod?: OptionData[];
  pickerType?: "date" | "month" | "quarter" | "year";
  dateFormat?: string;
  showPeriodSelector?: boolean;
  skipDateUpdateOnPeriodChange?: boolean;
  maxEndDate?: Date | null;
  minStartDate?: Date | null;
  addMenuPortal?: boolean;
  skipMaxEndDate?: boolean;
  minStartDateTooltip?: string;
};

export const DateFilter = ({
  updateFilter,
  values,
  datePeriod: datePeriodFromProps,
  pickerType = "date",
  dateFormat: dateFormatFromProps,
  showPeriodSelector = true,
  skipDateUpdateOnPeriodChange = false,
  maxEndDate = new Date(),
  minStartDate,
  addMenuPortal,
  skipMaxEndDate = false,
  minStartDateTooltip = "This date is before the minimum allowed date",
}: DateFilterProps) => {
  const datePeriod = [
    ...(datePeriodFromProps || defaultDatePeriod),
    { label: "Custom", value: "CUSTOM" },
  ];

  const defaultSelectedPeriod =
    datePeriod.find(({ value }) => value === values?.SELECT_PERIOD)?.value ||
    "CUSTOM";

  const initialDates = {
    START_DATE: values?.START_DATE
      ? dayjs(values.START_DATE as string).toString()
      : "",
    END_DATE: values?.END_DATE
      ? dayjs(values.END_DATE as string).toString()
      : "",
    SELECT_PERIOD: defaultSelectedPeriod,
  };

  const [selectedPeriod, setSelectedPeriod] = useState<string>(
    defaultSelectedPeriod
  );

  const { alertToast } = useToast();

  const picker = pickerTypeProps[pickerType];
  const dateFormat = dateFormatFromProps || dateFormats[pickerType];
  const placeholder = datePlaceholder[pickerType];
  const effectiveMaxEndDate = skipMaxEndDate ? undefined : maxEndDate;

  return (
    <Formik enableReinitialize initialValues={initialDates} onSubmit={() => {}}>
      {({ setFieldValue, values: { START_DATE, END_DATE } }) => {
        const handleDateChange = ({
          date,
          name,
        }: {
          date: Date;
          name: "START_DATE" | "END_DATE";
        }) => {
          setSelectedPeriod("CUSTOM");
          setFieldValue("SELECT_PERIOD", "CUSTOM");
          updateFilter("SELECT_PERIOD", "CUSTOM");

          // If pickerType is "quarter || month", set the END_DATE to the end of the month
          if (
            (pickerType === "quarter" || pickerType === "month") &&
            name === "END_DATE"
          ) {
            updateFilter(
              name,
              date ? dayjs(date).endOf("month").format(YYYY_MM_DD) : ""
            );
            return;
          }

          // If pickerType is "year", set the END_DATE to the end of the year
          if (name === "END_DATE" && pickerType === "year") {
            updateFilter(
              name,
              date ? dayjs(date).endOf("year").format(YYYY_MM_DD) : ""
            );
            return;
          }
          updateFilter(name, date ? dayjs(date).format(YYYY_MM_DD) : "");
        };

        const updateDates = (START_DATE?: Dayjs, END_DATE?: Dayjs) => {
          setFieldValue("START_DATE", START_DATE);
          setFieldValue("END_DATE", END_DATE);

          if (START_DATE) {
            updateFilter("START_DATE", dayjs(START_DATE).format(YYYY_MM_DD));
          }
          if (END_DATE) {
            updateFilter("END_DATE", dayjs(END_DATE).format(YYYY_MM_DD));
          }
        };

        const handleChange = (
          values: MultiValue<OptionData> | SingleValue<OptionData>
        ) => {
          if (values instanceof Array) {
            return;
          }

          if (values?.value) {
            setSelectedPeriod(values.value);
            updateFilter("SELECT_PERIOD", values.value);
            setFieldValue("SELECT_PERIOD", values.value);

            if (values?.value === "CUSTOM") {
              return;
            }
          }

          if (skipDateUpdateOnPeriodChange) {
            return;
          }

          const { startDate, endDate } = getDateRange(
            (values as SingleValue<OptionData>)?.value as DateRangeValue
          );

          updateDates(startDate, endDate);
        };

        return (
          <Form className="all:unset t-flex t-flex-col t-gap-4">
            {showPeriodSelector && (
              <>
                <Combobox
                  name="SELECT_PERIOD"
                  label="Period"
                  placeholder="Select period"
                  onChange={handleChange}
                  components={{ ClearIndicator: () => null }}
                  options={datePeriod}
                  block
                  value={datePeriod.find((d) => d.value === selectedPeriod)}
                  menuPortalTarget={addMenuPortal ? document.body : null}
                  tooltip
                  isOptionDisabled={(option) => Boolean(option.disableOption)}
                />
                <Divider />
              </>
            )}
            <Field name="START_DATE">
              {({ field }: FieldProps) => {
                return (
                  <DateInput
                    {...field}
                    {...picker}
                    dateFormat={dateFormat}
                    maxDate={
                      END_DATE
                        ? new Date(END_DATE)
                        : effectiveMaxEndDate || undefined
                    }
                    minDate={minStartDate || undefined}
                    getDisabledTooltipContent={(date) =>
                      Boolean(minStartDate) &&
                      dayjs(date).isBefore(minStartDate, "day") &&
                      minStartDateTooltip
                    }
                    label="From"
                    placeholder={placeholder}
                    onDateChange={(date) => {
                      if (
                        END_DATE &&
                        dayjs(date).isAfter(dayjs(END_DATE), "day")
                      ) {
                        alertToast({
                          message: "Start date cannot be greater than end date",
                        });
                        setFieldValue("END_DATE", "");
                        return;
                      }
                      handleDateChange({ date, name: "START_DATE" });
                    }}
                    portalId="callStart"
                  />
                );
              }}
            </Field>
            <Field name="END_DATE">
              {({ field }: FieldProps) => {
                return (
                  <DateInput
                    {...field}
                    {...picker}
                    dateFormat={dateFormat}
                    maxDate={effectiveMaxEndDate || undefined}
                    minDate={START_DATE ? new Date(START_DATE) : undefined}
                    label="To"
                    placeholder={placeholder}
                    onDateChange={(date) => {
                      if (
                        START_DATE &&
                        dayjs(date).isBefore(dayjs(START_DATE), "day")
                      ) {
                        alertToast({
                          message: "End date cannot be less than start date",
                        });
                        setFieldValue("START_DATE", "");
                        return;
                      }
                      handleDateChange({ date, name: "END_DATE" });
                    }}
                    portalId="callEnd"
                  />
                );
              }}
            </Field>
          </Form>
        );
      }}
    </Formik>
  );
};
